import React, { useLayoutEffect, useRef, useState } from 'react';
import { PANEL_BG, panelClass, PinnedFade } from './pinnedPanel';

/**
 * Jump-to strip for a long stack of per-index cards.
 *
 * With every index selected, Signal Analysis renders eighteen cards and the
 * only way to reach one was scrolling (Apal 2026-09-30). The chips in the
 * selector above cannot double as navigation — a click there already means
 * select / deselect, and the L+S tab beside it means something else again —
 * so navigation gets its own control, which sticks to the top of the scroll
 * area and marks the card you are currently reading.
 *
 * `items` are only the cards actually rendered: an index excluded from the
 * analysis has no card, so it has no chip either.
 *
 * WHY NOT `position: sticky`: admin's <main> is `overflow: auto` but never
 * actually scrolls — nothing bounds its height, so it grows with its content
 * and the WINDOW scrolls instead. Sticky binds to the nearest overflow
 * ancestor, i.e. to a <main> that never moves, so a sticky strip scrolled
 * straight off the page (verified in a reproduction of the layout: 776px
 * above the viewport). Rather than change the layout every admin page scrolls
 * by, the strip pins itself: once its slot passes the top it switches to
 * `position: fixed`, aligned to the slot's own left edge and width — so it
 * honours the collapsible sidebar — and it lets go after the last card.
 */
export const cardId = (symbol) => `sig-card-${symbol}`;

/**
 * Pinned, the panel sits FLUSH with the top edge. An earlier cut floated it
 * 8px down, and the page's text scrolled through that gap above it (Apal
 * 2026-09-30: "uncomfortable moving up/down and seeing text between browser
 * top and this new jump to bar").
 */
const PIN_GAP = 0;
/** Breathing room between the pinned panel and the card a jump lands on. */
const LAND_GAP = 12;

export default function IndexJumpBar({ items }) {
  const [active, setActive] = useState(items[0]?.symbol ?? null);
  // The parent rebuilds `items` on every render; key the observer on WHICH
  // cards exist, so it re-attaches when that changes and not on every paint.
  const key = items.map((it) => it.symbol).join(',');
  const slotRef = useRef(null);
  const barRef = useRef(null);
  const [pin, setPin] = useState(null);   // { left, width } while pinned
  const [barHeight, setBarHeight] = useState(0);
  // While a jump is gliding, keep the clicked chip lit instead of sweeping
  // the highlight across every card the scroll passes on the way.
  const jumping = useRef(null);   // { symbol, until }
  const settleTimer = useRef(null);

  useLayoutEffect(() => {
    const measure = () => {
      const slot = slotRef.current;
      const bar = barRef.current;
      if (!slot || !bar) return;
      const h = bar.offsetHeight;
      setBarHeight(h);
      const r = slot.getBoundingClientRect();
      const last = document.getElementById(cardId(items[items.length - 1]?.symbol));
      const listEnded = last ? last.getBoundingClientRect().bottom < h + PIN_GAP : false;
      setPin(r.top < PIN_GAP && !listEnded ? { left: r.left, width: r.width } : null);

      // Which card is being read: the LAST one whose top has passed the line
      // just under the panel. An intersection band marked Sensex while its
      // final sliver sat under the panel and Nifty 50 filled the screen. This
      // is the same line a jump lands a card on, so a jump always marks the
      // card it landed.
      const line = PIN_GAP + h + LAND_GAP + 4;
      let current = items[0]?.symbol ?? null;
      for (const it of items) {
        const el = document.getElementById(cardId(it.symbol));
        if (el && el.getBoundingClientRect().top <= line) current = it.symbol;
      }
      // At the bottom of the page the last cards can never scroll up to the
      // line — the page runs out first. Jumping to Ethereum used to mark
      // Bitcoin for exactly that reason. There, the card you jumped to wins,
      // and with no jump in flight, the last card does.
      // Only meaningful on a page that scrolls: a short page (two indexes on
      // a big monitor) is always "at the bottom", which would mark the last
      // card before the reader had moved at all.
      const doc = document.scrollingElement || document.documentElement;
      const scrollable = doc.scrollHeight > window.innerHeight + 2;
      const atBottom = scrollable && doc.scrollTop + window.innerHeight >= doc.scrollHeight - 2;
      const j = jumping.current;
      if (j) {
        if (current === j.symbol) jumping.current = null;             // landed
        else if (atBottom) { setActive(j.symbol); return; }          // cannot land any higher
        else if (Date.now() > j.until) jumping.current = null;        // reader took over
        else return;                                                  // still gliding
      } else if (atBottom && items.length) {
        current = items[items.length - 1].symbol;
      }
      setActive(current);
    };
    // The hold on a jumped-to chip ends when the glide actually stops — a
    // clock guessed at how long a glide takes and sometimes let go mid-way,
    // sweeping the highlight across the cards being passed. Each scroll
    // event re-arms a short timer; when scrolling goes quiet, the hold
    // releases and the marked card is recomputed from where things landed.
    const onScroll = () => {
      measure();
      if (!jumping.current) return;
      clearTimeout(settleTimer.current);
      settleTimer.current = setTimeout(() => { jumping.current = null; measure(); }, 180);
    };
    measure();
    // capture: scroll does not bubble, but a capturing listener on window
    // hears it from whichever element is doing the scrolling.
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', measure);
    // Collapsing the sidebar moves and widens the content column without
    // firing scroll or resize, which left a pinned panel at its old place.
    // Watching the slot's own size catches every such change.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
    if (ro && slotRef.current) ro.observe(slotRef.current);
    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', measure);
      ro?.disconnect();
      clearTimeout(settleTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (items.length < 2) return null;   // nothing to jump between

  const go = (symbol) => {
    const el = document.getElementById(cardId(symbol));
    if (!el) return;
    setActive(symbol);
    // `until` is only a backstop, for a jump that produces no scroll at all
    // (already in place); normally the settle timer releases the hold.
    jumping.current = { symbol, until: Date.now() + 2500 };
    // Land the card BELOW the pinned panel. Its height is not fixed — two or
    // three rows depending on the width and how many indexes are selected —
    // so a static scroll-margin put the card's top 2px under the panel.
    // Measure it now, at the moment of the jump.
    const h = barRef.current?.offsetHeight ?? 0;
    el.style.scrollMarginTop = `${PIN_GAP + h + LAND_GAP}px`;
    // Glide, unless the reader has asked their OS for less motion.
    const reduce = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });   // here the page SHOULD move
  };

  // Group consecutive items by region, keeping the order the cards are in.
  const groups = [];
  items.forEach((it) => {
    const g = groups[groups.length - 1];
    if (g && g.region === it.region) g.items.push(it);
    else groups.push({ region: it.region, label: it.regionLabel || it.region, items: [it] });
  });

  return (
    // The slot holds the panel's place in the flow, so pinning it does not
    // make the cards below jump up by its height.
    <div ref={slotRef} className="mb-6" style={pin ? { minHeight: barHeight } : undefined}>
      <div
        ref={barRef}
        data-testid="index-jump-bar"
        data-pinned={pin ? 'true' : undefined}
        // Opaque, square to the top edge when pinned — the shared panel look
        // (components/pinnedPanel.js), same as Best Trades' highlight panel.
        style={pin ? { position: 'fixed', top: PIN_GAP, left: pin.left, width: pin.width, background: PANEL_BG } : { background: PANEL_BG }}
        className={`z-20 px-4 py-3 transition-shadow ${panelClass(!!pin)}`}
      >
        <div className="flex items-start gap-4">
          <span className="text-[11px] uppercase tracking-widest text-dark-400 font-semibold pt-2 shrink-0">
            Jump to
          </span>
          {/* Region names, not hairlines: the selector above already speaks
              in INDIA / US / CRYPTO rows, and a named group is scannable
              where an unlabelled divider is not (Apal 2026-09-30). */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 min-w-0">
            {groups.map((g) => (
              <div key={g.region} className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-dark-500 font-semibold mr-1">
                  {g.label}
                </span>
                {g.items.map((it) => {
                  const on = it.symbol === active;
                  return (
                    <button
                      key={it.symbol}
                      type="button"
                      data-chip={it.symbol}
                      aria-current={on ? 'true' : undefined}
                      onClick={() => go(it.symbol)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${
                        on
                          ? 'bg-primary-600 text-white shadow-md shadow-primary-900/40'
                          : 'bg-dark-700/60 text-dark-200 hover:bg-dark-600 hover:text-white'
                      }`}
                    >
                      {it.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <PinnedFade show={!!pin} />
      </div>
    </div>
  );
}
